import { useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactCard from '../components/ContactCard';

const webStats = [
  {
    label: 'Faster launches',
    value: '14 days',
    detail: 'Average time to go live with a conversion-ready microsite or landing experience.',
  },
  {
    label: 'Conversion lift',
    value: '+72%',
    detail: 'Median increase in lead submissions after our redesign and CRO sprints.',
  },
  {
    label: 'Performance score',
    value: '90+',
    detail: 'Lighthouse scores we hit across mobile & desktop for every build we ship.',
  },
];

const webOffers = [
  {
    title: 'Strategy & UX architecture',
    copy: 'Messaging workshops, sitemap planning, and wireframes that showcase your value and guide visitors to convert.',
  },
  {
    title: 'Design & custom development',
    copy: 'Pixel-perfect Figma designs paired with component-based builds using React, Next.js, or the stack that suits you.',
  },
  {
    title: 'Conversion optimisation baked in',
    copy: 'CRO testing, analytics, and automation ensure your site stays optimised long after launch.',
  },
];

const webProcess = [
  {
    title: 'Discover',
    text: 'Stakeholder interviews, competitive analysis, and analytics audit uncover growth opportunities.',
  },
  {
    title: 'Design',
    text: 'Collaborative UX/UI sprints produce conversion-focused layouts with premium visuals and copy.',
  },
  {
    title: 'Develop',
    text: 'We build on modern frameworks, integrate your tools, and stress-test performance before launch.',
  },
  {
    title: 'Iterate',
    text: 'Post-launch optimisation ensures the experience keeps converting with A/B tests and CRO experiments.',
  },
];

export default function WebDevLanding() {
  const contactRef = useRef(null);
  const handleTalkClick = () => {
    contactRef.current?.focusForm?.();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-white via-sky-50 to-indigo-100 font-sans text-slate-900">
      <Navbar onTalkClick={handleTalkClick} />

      <main className="flex-1 pt-32">
        <section id="services" className="mx-auto max-w-6xl px-6">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">High-converting web experiences</p>
              <h1 className="mt-4 text-4xl font-semibold md:text-5xl">Websites engineered to impress and convert</h1>
              <p className="mt-4 text-lg text-slate-600">
                We plan, design, and build lightning-fast sites that tell your story, build trust, and turn visitors into
                booked calls, demos, and sales.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleTalkClick}
                  className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow hover:opacity-90"
                >
                  Book a web strategy session →
                </button>
                <a
                  href="tel:14374944736"
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-white"
                >
                  Talk now: 1‑437‑494‑4736
                </a>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {webStats.map((stat) => (
                <div key={stat.label} className="rounded-3xl border border-slate-200 bg-white/70 p-5 text-center shadow-sm backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{stat.label}</p>
                  <p className="mt-3 text-3xl font-semibold text-slate-900">{stat.value}</p>
                  <p className="mt-2 text-xs text-slate-500">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-6xl px-6">
          <div className="grid gap-6 md:grid-cols-3">
            {webOffers.map((offer) => (
              <article
                key={offer.title}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <h3 className="text-xl font-semibold text-slate-900">{offer.title}</h3>
                <p className="mt-3 text-sm text-slate-600">{offer.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="work" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900">Recent website launches</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">M2 Air Solution</h3>
                <p className="mt-3 text-sm text-slate-600">
                  We modernised their brand identity, built a component-based site, and integrated online booking to
                  drive 124 leads in the first month.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">PSA Toronto</h3>
                <p className="mt-3 text-sm text-slate-600">
                  A conversion-focused redesign and membership portal that increased paid trials by 63 in two weeks.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="process" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900">Our build + scale process</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-4">
              {webProcess.map((phase) => (
                <div key={phase.title} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">{phase.title}</h3>
                  <p className="mt-3 text-sm text-slate-600">{phase.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-4xl px-6 text-center">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">Let’s build a site your customers love</h2>
            <p className="mt-3 text-lg text-slate-600">
              Share your goals and we’ll bring mockups, timelines, and a roadmap to a no-pressure strategy session.
            </p>
            <button
              type="button"
              onClick={handleTalkClick}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-white shadow hover:opacity-90"
            >
              Plan my web project →
            </button>
          </div>
        </section>
      </main>

      <ContactCard ref={contactRef} className="mt-16" />
      <Footer />
    </div>
  );
}
